// src/prototype/components/sep/SEPSectionExecutionSequencing.tsx

import React from 'react';
import { CalendarDays } from 'lucide-react';
import { effortTierForBadge } from '../../config/opportunityBadge';

interface Finalist {
  id: string;
  title: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
}

export interface SEPSectionExecutionSequencingProps {
  finalists: Finalist[];
  clientName: string;
}

const MONTHS = ['Q1 M1', 'Q1 M2', 'Q1 M3', 'Q2 M4', 'Q2 M5', 'Q2 M6', 'Q3 M7', 'Q3 M8', 'Q3 M9', 'Q4 M10', 'Q4 M11', 'Q4 M12'];
const COLORS = ['#18365E', '#FF6E4C', '#2563EB'];

const getTimingFromEffort = (effort: string, idx: number): { start: number; duration: number; phase: string[] } => {
  if (effort === 'Low') return { start: 0, duration: 3, phase: ['Discovery', 'Pilot', 'Launch'] };
  if (effort === 'High') return { start: idx * 1, duration: 10, phase: ['Discovery', 'Design', 'Build', 'Pilot', 'Scale'] };
  return { start: Math.min(idx, 2), duration: 6, phase: ['Discovery', 'Build', 'Pilot', 'Launch'] };
};

const PhaseLabel: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div className="flex items-center gap-1.5">
    <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
    <span className="text-[10px] text-[#717182]">{label}</span>
  </div>
);

export const SEPSectionExecutionSequencing: React.FC<SEPSectionExecutionSequencingProps> = ({ finalists, clientName }) => (
  <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
    <div className="flex items-start gap-3 mb-6">
      <div className="w-[3px] self-stretch rounded-full bg-[#FF6E4C] shrink-0 min-h-[40px]" />
      <div>
        <p className="text-[10px] font-bold text-[#FF6E4C] uppercase tracking-widest mb-0.5">Section 5</p>
        <h2 className="text-lg font-bold text-[#18365E] leading-tight">Execution Sequencing</h2>
        <p className="text-[11px] text-[#717182] mt-0.5">12-month delivery timeline across all portfolio initiatives</p>
      </div>
    </div>

    <p className="text-sm text-[#18365E]/80 leading-relaxed mb-6">
      The sequencing plan below shows the recommended delivery timeline for <strong>{clientName}</strong>'s initiative portfolio across a 12-month horizon. Each initiative is phased to manage organizational capacity while maximizing early value delivery.
    </p>

    {/* Gantt */}
    <div className="overflow-x-auto">
      <div className="min-w-[640px]">
        {/* Month headers */}
        <div className="flex mb-2">
          <div className="w-[180px] shrink-0" />
          <div className="flex-1 grid grid-cols-12 gap-0">
            {MONTHS.map((m, i) => (
              <div key={i} className={`text-center text-[9px] font-semibold py-1 ${i % 3 === 0 ? 'text-[#18365E]' : 'text-[#717182]/60'}`}>
                {i % 3 === 0 ? m.split(' ')[0] : m.split(' ')[1]}
              </div>
            ))}
          </div>
        </div>

        {/* Quarter dividers */}
        <div className="flex mb-3">
          <div className="w-[180px] shrink-0" />
          <div className="flex-1 grid grid-cols-4 gap-0 border border-[#e5e7eb] rounded-lg overflow-hidden">
            {['Q1', 'Q2', 'Q3', 'Q4'].map(q => (
              <div key={q} className="text-center text-[9px] font-bold text-[#18365E]/40 py-1 border-r border-[#e5e7eb] last:border-0 bg-[#F5F7FA]">{q}</div>
            ))}
          </div>
        </div>

        {/* Initiative rows */}
        <div className="space-y-2">
          {finalists.map((f, idx) => {
            // Sequencing keys on the ROOT-derived effort tier, not the stored string.
            const { start, duration, phase } = getTimingFromEffort(effortTierForBadge({ id: f.id, implementationEffort: f.implementationEffort }), idx);
            const color = COLORS[idx % COLORS.length];
            const phaseCols = duration / phase.length;

            return (
              <div key={f.id} className="flex items-center gap-0">
                <div className="w-[180px] shrink-0 pr-3">
                  <p className="text-xs font-semibold text-[#18365E] leading-tight line-clamp-2">{f.title}</p>
                  <p className="text-[10px] text-[#717182] mt-0.5">{f.timeToValue}</p>
                </div>
                <div className="flex-1 relative h-8">
                  {/* Background track */}
                  <div className="absolute inset-0 grid grid-cols-12 gap-0">
                    {MONTHS.map((_, i) => (
                      <div key={i} className={`h-full ${i % 3 === 0 ? 'bg-[#F5F7FA]' : 'bg-white'} border-r border-[#e5e7eb]/50 last:border-0`} />
                    ))}
                  </div>
                  {/* Bar */}
                  <div
                    className="absolute top-1 bottom-1 rounded-lg flex overflow-hidden"
                    style={{
                      left: `${(start / 12) * 100}%`,
                      width: `${(duration / 12) * 100}%`,
                    }}
                  >
                    {phase.map((p, pi) => (
                      <div
                        key={p}
                        className="flex-1 flex items-center justify-center text-white text-[9px] font-semibold"
                        style={{
                          background: color,
                          opacity: 0.4 + (pi / phase.length) * 0.6,
                          borderRight: pi < phase.length - 1 ? '1px solid rgba(255,255,255,0.3)' : undefined,
                        }}
                      >
                        {phaseCols >= 1 ? p : ''}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-5 flex items-center gap-5 flex-wrap">
          {finalists.map((f, idx) => (
            <PhaseLabel key={f.id} label={f.title.split(' ').slice(0, 3).join(' ') + '…'} color={COLORS[idx % COLORS.length]} />
          ))}
        </div>
      </div>
    </div>

    {/* Sequencing notes */}
    <div className="mt-5 bg-[#F5F7FA] border border-[#e5e7eb] rounded-xl p-4">
      <p className="text-[10px] font-semibold text-[#717182] uppercase tracking-wide mb-2">Sequencing Principles</p>
      <div className="space-y-1.5">
        {[
          'Quick Win initiatives launch in Q1 to generate early organizational confidence and measurable ROI.',
          'Moderate-effort programs begin after Q1 infrastructure is established, with pilot phases in Q2.',
          'High-complexity initiatives use phased delivery to reduce commitment risk and enable course correction.',
          'Portfolio milestones are structured to deliver value at the end of each quarter.',
        ].map((note, i) => (
          <div key={i} className="flex items-start gap-2">
            <div className="w-1 h-1 rounded-full bg-[#FF6E4C] mt-1.5 shrink-0" />
            <p className="text-xs text-[#18365E]/75 leading-relaxed">{note}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default SEPSectionExecutionSequencing;